import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { List, X, UserCircle, SignOut, Sparkle, Heart, ChatCircle, Crown, Gauge, ShieldStar, UsersThree } from "@phosphor-icons/react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = (p) => loc.pathname === p;

  const publicLinks = [
    { to: "/", label: "Home" },
    { to: "/plans", label: "Membership" },
    { to: "/#stories", label: "Stories" },
  ];

  const authedLinks = [
    { to: "/dashboard", label: "Dashboard", icon: Gauge },
    { to: "/browse", label: "Browse", icon: Sparkle },
    { to: "/interests", label: "Interests", icon: Heart },
    { to: "/messages", label: "Messages", icon: ChatCircle },
    { to: "/plans", label: "Membership", icon: Crown },
  ];

  const links = user ? authedLinks : publicLinks;

  return (
    <header
      className="sticky top-0 z-40 glass border-b border-brand-line"
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 h-20">
        <Link to={user ? "/dashboard" : "/"} className="flex items-baseline gap-2" data-testid="brand-logo">
          <span className="font-serif text-3xl tracking-tight text-brand-ink">WR</span>
          <span className="label-eyebrow text-brand-terracotta">Rishtey</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`px-4 py-2 text-sm font-medium transition-colors duration-200 hover:text-brand-terracotta ${isActive(l.to) ? "text-brand-terracotta" : "text-brand-ink"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="flex items-center gap-2 px-3 py-2 rounded-full border border-brand-line hover:border-brand-terracotta/40 transition-colors">
                  <UserCircle size={22} weight="duotone" className="text-brand-terracotta" />
                  <span className="text-sm font-medium text-brand-ink">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => nav("/my-profile")} data-testid="menu-my-profile">
                  <UserCircle size={18} className="mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/shortlist")} data-testid="menu-shortlist">
                  <Heart size={18} className="mr-2" /> Shortlist
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/invite")} data-testid="menu-invite">
                  <UsersThree size={18} className="mr-2" /> Invite family
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/plans")} data-testid="menu-plans">
                  <Crown size={18} className="mr-2" /> Membership
                </DropdownMenuItem>
                {["admin", "moderator", "super_admin"].includes(user.role) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                      <ShieldStar size={18} className="mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout">
                  <SignOut size={18} className="mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => nav("/login")}
                data-testid="nav-login-btn"
                className="rounded-full text-brand-ink hover:text-brand-terracotta"
              >
                Sign In
              </Button>
              <Button
                onClick={() => nav("/register")}
                data-testid="nav-register-btn"
                className="rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white px-6"
              >
                Create Profile
              </Button>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-brand-bone border-t border-brand-line px-6 py-4 space-y-2">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 text-brand-ink">
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link to="/my-profile" onClick={() => setOpen(false)} className="block py-2">My Profile</Link>
              {["admin", "moderator", "super_admin"].includes(user.role) && <Link to="/admin" onClick={() => setOpen(false)} className="block py-2">Admin</Link>}
              <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="block py-2 text-brand-terracotta">Sign Out</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="block py-2">Sign In</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="block py-2 text-brand-terracotta">Create Profile</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
